<template>
  <div>
    <modal
      v-if="show"
      :extra-wide="true"
      :title="i18n.t('/Company/CompanyZoekenModal/Title')"
      @close="close"
    >
      <dealer-company-search
        v-if="step < 2"
        @has-searched-dealer-companies="onSearchedDealerCompanies"
      />
      <national-company-search
        v-else-if="step >= 2 && step <= 4"
        @has-searched-national-companies="onSearchedNationalCompanies"
      />
      <company-form v-else-if="step == 5" @on-submit="close" />

      <company-card
        v-if="step == 0 && hasRecommendedCompany"
        :company="recommendedCompany"
        @select-result="selectCompany"
      />

      <div v-if="step > 0">
        <ul v-if="foundCompanies.length">
          <li v-for="(company, i) in companiesToShow" :key="i">
            <company-card :company="company" @select-result="selectCompany" />
          </li>
        </ul>
        <div v-if="showNoResultsMessage">
          <p>{{ i18n.t('/Company/CompanyZoekenModal/GeenCompanyGevonden') }}</p>
        </div>

        <app-button
          v-if="(step == 1 || step >= 3) && amountToShow <= foundCompanies.length"
          data-qa="show-more-companies-button"
          @click="showMoreResults()"
        >
          {{ i18n.t('/Company/CompanyZoekenModal/ToonMeer') }}
        </app-button>

        <app-button
          v-if="step == 1"
          data-qa="search-companies-national-button"
          @click="searchNational"
        >
          {{ i18n.t('/Company/CompanyZoekenModal/ZoekNationaal') }}
        </app-button>

        <app-button
          v-if="step >= 3 && step < 5"
          data-qa="search-company-back-button"
          @click="step = 1"
        >
          {{ i18n.t('/Company/CompanyZoekenModal/TerugKnop') }}
        </app-button>

        <app-button v-if="step == 4" data-qa="add-company-button" @click="step = 5">
          {{ i18n.t('/Company/CompanyZoekenModal/CompanyAanmaken') }}
        </app-button>
      </div>
    </modal>
  </div>
</template>

<script>
import Modal from '@/components/generic/presentational/molecules/modal/Modal.vue'
import DealerCompanySearch from '@/components/generic/presentational/molecules/companySearch/DealerCompanySearch.vue'
import NationalCompanySearch from '@/components/generic/presentational/molecules/companySearch/NationalCompanySearch.vue'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import CompanyCard from '@/components/generic/presentational/molecules/companyCard/CompanyCard.vue'
import StoreConnector from '@/helpers/StoreConnector'
import CompanyForm from '@/components/generic/presentational/organisms/companyForm/CompanyForm.vue'
import { EventBus } from '@/plugins/event-bus'
import { mapGetters } from 'vuex'
import { pageTypes } from '@/helpers/pageTypeConstants'

export default {
  components: {
    Modal,
    DealerCompanySearch,
    NationalCompanySearch,
    CompanyCard,
    AppButton,
    CompanyForm,
  },
  provide() {
    return {
      isNationalCompanySearch: this.isNationalCompanySearch,
    }
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    pageType: {
      type: String,
      default: pageTypes.PURCHASE_ORDER,
      validator(value) {
        return Object.values(pageTypes).includes(value)
      },
    },
  },
  emits: ['close'],
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      hasSearched: false,
      amountToShow: 20,
      foundCompanies: [],
      recommendedCompany: null,
      step: 0,
      hasSearchedNationalCompanies: false,
      selectedCompany: null,
      isLoading: false,
    }
  },
  computed: {
    ...mapGetters({
      customer: 'quote/customer',
      purchaseCustomer: 'purchaseOrder/customer',
      dealerSettings: 'user/dealerSettings',
    }),
    customerCompanyMagicNumber() {
      if (this.pageType === pageTypes.PURCHASE_ORDER) {
        return this.purchaseCustomer?.CustomerCompanyMagicNumber
      }
      if (this.pageType === pageTypes.QUOTE) {
        return this.customer?.CustomerCompanyMagicNumber
      }
      return ''
    },
    customerMagicNumber() {
      if (this.pageType === pageTypes.PURCHASE_ORDER) {
        return this.purchaseCustomer?.CustomerMagicNumber
      }
      if (this.pageType === pageTypes.QUOTE) {
        return this.customer?.CustomerMagicNumber
      }
      return ''
    },
    showNoResultsMessage() {
      return (
        this.foundCompanies.length == 0 &&
        (this.hasSearchedDealerCompanies || this.hasSearchedNationalCompanies)
      )
    },
    companiesToShow() {
      return this.foundCompanies.filter((_, index) => index < this.amountToShow)
    },
    hasRecommendedCompany() {
      return this.recommendedCompany != null
    },
  },
  created() {
    StoreConnector.get('companies/searchResults', (data) => {
      this.foundCompanies = data
    })

    StoreConnector.get('companies/recommendedCompany', (data) => {
      this.recommendedCompany = data
    })

    if (!this.dealerSettings.autolineKeyloopEnabled) {
      this.searchNational()
    } else {
      this.step = 0
    }
  },
  methods: {
    close() {
      if (!this.dealerSettings.autolineKeyloopEnabled) {
        this.searchNational()
      } else {
        this.step = 0
      }
      this.amountToShow = 20
      StoreConnector.dispatch('companies/resetSearchResults')
      this.$emit('close')
    },
    selectCompany(company) {
      this.selectedCompany = company
      this.setCompany()
    },
    getNameSpace() {
      if (this.pageType === pageTypes.PURCHASE_ORDER) {
        return 'purchaseOrder'
      }
      if (this.pageType === pageTypes.QUOTE) {
        return 'quote'
      }
      if (this.pageType === pageTypes.CHANGE_ORDER) {
        return 'changeOrder'
      }
    },
    setCompany() {
      const namespace = this.getNameSpace()

      StoreConnector.dispatch(`${namespace}/setCustomerCompany`, this.selectedCompany).then(() => {
        EventBus.$emit(`${namespace}/companyUpdated`)
      })
      this.selectedCompany = null
      this.close()
    },
    onSearchedDealerCompanies() {
      this.step = 1
      this.hasSearchedDealerCompanies = true
    },
    onSearchedNationalCompanies() {
      this.step = 4
      this.hasSearchedNationalCompanies = true
    },
    showMoreResults() {
      this.amountToShow += 20
    },
    searchNational() {
      this.step = 3
      this.hasSearchedDealerCompanies = false
      StoreConnector.dispatch('companies/resetSearchResults')
    },
    isNationalCompanySearch() {
      return this.hasSearchedNationalCompanies
    },
  },
}
</script>

<style lang="scss" scoped>
ul {
  @include list-reset;
}
</style>
